import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from 'react';

const STORAGE_KEY = 'trendhome_holiday_snow';

interface HolidayThemeContextType {
  snowEnabled: boolean;
  toggleSnow: () => void;
  setSnowEnabled: (value: boolean) => void;
}

const HolidayThemeContext = createContext<HolidayThemeContextType | undefined>(undefined);

export const useHolidayTheme = (): HolidayThemeContextType => {
  const context = useContext(HolidayThemeContext);
  if (context === undefined) {
    throw new Error('useHolidayTheme must be used within a HolidayThemeProvider');
  }
  return context;
};

interface HolidayThemeProviderProps {
  children: ReactNode;
}

export const HolidayThemeProvider: React.FC<HolidayThemeProviderProps> = ({ children }) => {
  const [snowEnabled, setSnowEnabledState] = useState<boolean>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      // Default on for the New Year season; only off if user explicitly disabled.
      if (stored === '0') return false;
      if (stored === '1') return true;
      return true;
    } catch {
      return true;
    }
  });

  const persist = useCallback((value: boolean) => {
    try {
      localStorage.setItem(STORAGE_KEY, value ? '1' : '0');
    } catch {
      // ignore storage errors
    }
  }, []);

  const setSnowEnabled = useCallback((value: boolean) => {
    setSnowEnabledState(value);
    persist(value);
  }, [persist]);

  const toggleSnow = useCallback(() => {
    setSnowEnabledState((prev) => {
      const next = !prev;
      persist(next);
      return next;
    });
  }, [persist]);

  useEffect(() => {
    const root = document.documentElement;
    if (snowEnabled) {
      root.setAttribute('data-skin', 'snow');
    } else {
      root.removeAttribute('data-skin');
    }
    return () => {
      root.removeAttribute('data-skin');
    };
  }, [snowEnabled]);

  return (
    <HolidayThemeContext.Provider value={{ snowEnabled, toggleSnow, setSnowEnabled }}>
      {children}
    </HolidayThemeContext.Provider>
  );
};
